import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { Info, Zap } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { URDFViewer } from '@/components/ui/urdf-viewer';
import { CheckpointPanel } from '@/features/checkpoint/checkpoint-panel';
import { JogPanel } from '@shared/components/navigation/jog-panel';
import { MinimalCameraViewer } from '@shared/components/camera/minimal-camera-viewer';
import { isCloud } from '@shared/lib/constants';
import { robotId as urlRobotId } from '@shared/lib/config';
import { useCurrentRobotKey } from '@/hooks/useCurrentRobot';
import { useRobotStore } from '@/stores/useRobotStore';
import { useActionError } from '@/stores/useActionError';
import {
  cancelNavigation,
  clearEstop,
  emergencyStop,
  endTeleop,
  startTeleop,
  teleopJog,
  undock,
} from '@/lib/navigation-api';
import { getAuthToken } from '@/lib/authToken';

const PROD_HOST = 'https://dashboard.vernerobotics.com';

/** Inline replacement for the JogPanel when the dashboard isn't on a cloud
 *  host. Phones running on the cloud are where actual jogging should happen;
 *  the robot kiosk / dev laptop just shows a QR that opens this same robot's
 *  joystick page on a phone. */
function JogQrPanel() {
  const fleetRobotId = useCurrentRobotKey();
  const robotId = urlRobotId ?? fleetRobotId;
  const url = robotId
    ? `${PROD_HOST}/?robot_id=${encodeURIComponent(robotId)}#/joystick`
    : null;

  return (
    <div className="w-full rounded-md border border-border bg-surface/40 px-4 py-5 flex flex-col items-center gap-3">
      <span className="text-xs text-muted text-center">Scan to drive on phone</span>
      {url ? (
        <>
          <div className="bg-white p-3 rounded-xl">
            <QRCodeSVG value={url} size={180} level="M" />
          </div>
          <code className="text-[10px] text-muted break-all max-w-full text-center leading-tight">{url}</code>
        </>
      ) : (
        <span className="text-xs text-muted">No robot selected</span>
      )}
    </div>
  );
}

export function useTeleopUser(): string {
  const auth = useAuth();
  const email = auth.user?.profile?.email;
  return typeof email === 'string' && email ? email : 'local';
}

function formatHours(hrs: number): string {
  if (hrs < 1) return `${Math.round(hrs * 60)}m`;
  return `${hrs.toFixed(1)}h`;
}

export function BatteryIcon() {
  const battery = useRobotStore((s) => s.battery);
  const pct = battery?.battery_pct ?? null;
  if (pct == null) return null;
  const clamped = Math.max(0, Math.min(100, pct));
  const isLow = clamped <= 20;
  const isCharging = battery?.charging ?? false;
  const timeLabel = battery?.time_to_full_hrs != null && isCharging
    ? `${formatHours(battery.time_to_full_hrs)} to full`
    : battery?.remaining_output_hrs != null
      ? `${formatHours(battery.remaining_output_hrs)} remaining`
      : null;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        <div className="relative w-9 h-[18px] rounded-[3px] border-2 border-foreground/30 p-[2px]">
          <div
            className={`h-full rounded-[1px] transition-[width] duration-500 ${
              isCharging ? 'bg-success' : isLow ? 'bg-danger' : 'bg-accent'
            }`}
            style={{ width: `${clamped}%` }}
          />
          {isCharging && (
            <Zap
              className="absolute inset-0 m-auto h-3 w-3 text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
              fill="currentColor"
              strokeWidth={1.5}
            />
          )}
        </div>
        <div className="w-[3px] h-[8px] rounded-r-sm bg-foreground/30" />
      </div>
      <span className={`text-sm font-semibold tabular-nums ${isLow && !isCharging ? 'text-danger' : 'text-foreground'}`}>
        {clamped}%
      </span>
      {isCharging && (
        <span className="text-xs font-semibold text-success">Charging</span>
      )}
      {timeLabel && (
        <span className="text-xs text-muted">{timeLabel}</span>
      )}
    </div>
  );
}

export function EStopButton() {
  const estopActive = useRobotStore((s) => s.estopActive);
  const baseClass =
    'w-full h-14 rounded-md active:scale-95 border-4 shadow-lg transition-all flex items-center justify-center';
  if (estopActive) {
    return (
      <button
        className={`${baseClass} bg-orange-500 hover:bg-orange-400 border-orange-700 shadow-orange-900/50`}
        aria-label="Reset Emergency Stop"
        onClick={() => clearEstop()}
      >
        <span className="text-white font-black text-base tracking-widest uppercase select-none whitespace-nowrap">
          Reset E-Stop
        </span>
      </button>
    );
  }
  return (
    <button
      className={`${baseClass} bg-red-600 hover:bg-red-500 border-red-800 shadow-red-900/50`}
      aria-label="Emergency Stop"
      onClick={() => emergencyStop()}
    >
      <span className="text-white font-black text-base tracking-widest uppercase select-none whitespace-nowrap">
        Emergency Stop
      </span>
    </button>
  );
}

export function NavigationReadouts({ renderJogSlot }: { renderJogSlot?: () => ReactNode } = {}) {
  const navStatus = useRobotStore((s) => s.navStatus);
  const baseOwner = useRobotStore((s) => s.baseOwner);
  const teleopUser = useRobotStore((s) => s.teleopUser);
  const cameras = useRobotStore((s) => s.cameras);
  const cameraViewerActive = useRobotStore((s) => s.cameraViewerActive);
  const selectedCameraIds = useRobotStore((s) => s.selectedCameraIds);
  const setCameraViewerActive = useRobotStore((s) => s.setCameraViewerActive);
  const toggleCameraSelection = useRobotStore((s) => s.toggleCameraSelection);
  const clearSelectedCameras = useRobotStore((s) => s.clearSelectedCameras);
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const token = getAuthToken();
  const currentUser = useTeleopUser();
  const runAction = useActionError((s) => s.runAction);

  const navPayload = useMemo(() => {
    const navHb = heartbeats.find((hb) => hb.heartbeat_id === 'navigation');
    return (navHb?.payload ?? {}) as {
      dock_status?: string;
      nav_distance_remaining?: number;
      velocity_mps?: number;
      nav_failure_reason?: string | null;
      dock_error?: string | null;
    };
  }, [heartbeats]);
  const dockStatus = navPayload.dock_status ?? null;
  const distanceRemaining = navPayload.nav_distance_remaining;
  const velocityMps = navPayload.velocity_mps;
  const isDocked = dockStatus === 'docked';
  const navFailureReason = navPayload.nav_failure_reason || null;
  const dockError = navPayload.dock_error || null;

  const activeWaypoint = navStatus != null && ['pending', 'active', 'cancelling'].includes(navStatus);

  const roleLabel =
    baseOwner === 'navigation' && navStatus === 'active'
      ? 'Autonomous Navigation'
      : baseOwner === 'teleop'
        ? 'User is Driving'
        : 'Navigation';

  const [undocking, setUndocking] = useState(false);

  const defaultJog = () => (
    isCloud ? (
      <JogPanel
        baseOwner={baseOwner}
        teleopUser={teleopUser}
        currentUser={currentUser}
        activeWaypoint={activeWaypoint}
        onJog={(vx, vy, vw) => {
          teleopJog(currentUser, vx, vy, vw).catch(() => {});
        }}
        onToggleTeleop={(overridden) => {
          runAction(() => (overridden ? endTeleop(currentUser) : startTeleop(currentUser)));
        }}
        renderCamera={() => (
          <MinimalCameraViewer
            cameras={cameras}
            active={cameraViewerActive}
            selectedCameraId={selectedCameraIds[0] ?? null}
            onActivate={() => setCameraViewerActive(true)}
            onDeactivate={() => setCameraViewerActive(false)}
            onSelectCamera={(id) => {
              clearSelectedCameras();
              toggleCameraSelection(id);
            }}
            showControls="always"
            token={token}
          />
        )}
      />
    ) : (
      <JogQrPanel />
    )
  );

  return (
    <div className="w-full max-w-md flex flex-col gap-3">
      <NavTimeoutCountdown />
      {navFailureReason && (
        <div className="relative pl-5 pr-3 py-2.5 bg-gradient-to-r from-danger/15 to-transparent text-sm text-danger flex flex-col gap-1">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-danger" />
          <div className="font-semibold">Error During Navigation:</div>
          {navFailureReason === 'Planner: no valid path found.' ? (
            <div className="flex flex-col gap-1">
              <div>The robot was unable to plan a path to the destination.</div>
              <div>Common reasons:</div>
              <div>1) There are obstacles in the way.</div>
              <div>2) The destination is inside an obstacle on the map.</div>
            </div>
          ) : (
            <div>{navFailureReason}</div>
          )}
        </div>
      )}
      {dockError && (
        <div className="relative pl-5 pr-3 py-2.5 bg-gradient-to-r from-danger/15 to-transparent text-sm text-danger flex flex-col gap-1">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-danger" />
          <div className="font-semibold">Error During Docking:</div>
          {dockError === 'undock_timeout' ? (
            <div>Docking timed out. Something was most likely detected behind the robot.</div>
          ) : (
            <div>{dockError}</div>
          )}
        </div>
      )}
      <div className="border-y border-border/40 py-3 flex flex-col gap-2">
        <div>
          <div className="text-xs text-muted">Driving Status</div>
          <div className="text-base font-semibold text-foreground">{roleLabel}</div>
        </div>
        <div className="flex items-stretch gap-4">
          <div className="flex-1">
            <div className="text-xs text-muted">Velocity</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-mono font-semibold text-foreground tabular-nums">
                {velocityMps != null ? Number(velocityMps).toFixed(2) : 'N/A'}
              </span>
              <span className="text-xs text-muted">m/s</span>
            </div>
          </div>
          <div className="w-px bg-border/40" />
          <div className="flex-1">
            <div className="text-xs text-muted">Distance Remaining</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-mono font-semibold text-foreground tabular-nums">
                {distanceRemaining != null ? Number(distanceRemaining).toFixed(1) : 'N/A'}
              </span>
              {distanceRemaining != null && <span className="text-xs text-muted">m</span>}
            </div>
          </div>
        </div>
      </div>

      {navStatus === 'active' && (
        <button
          onClick={() => cancelNavigation()}
          className="w-full rounded-md border border-danger/40 bg-danger/12 px-4 py-3 text-base font-semibold text-danger hover:bg-danger/20 transition"
        >
          Cancel Waypoint
        </button>
      )}

      {isDocked ? (
        <button
          disabled={undocking}
          onClick={() => {
            setUndocking(true);
            undock().finally(() => setUndocking(false));
          }}
          className="w-full rounded-md bg-accent px-4 py-3 text-base font-semibold text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {undocking ? 'Undocking…' : 'Undock'}
        </button>
      ) : (
        renderJogSlot ? renderJogSlot() : defaultJog()
      )}
    </div>
  );
}

function NavigationControls() {
  return (
    <div className="h-full w-full flex items-center justify-center p-4">
      <NavigationReadouts />
    </div>
  );
}

function CountdownCircle({ remaining, total }: { remaining: number; total: number }) {
  const progress = Math.max(0, Math.min(1, remaining / total));
  const r = 9;
  const c = 2 * Math.PI * r;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="-rotate-90 shrink-0">
      <circle cx="12" cy="12" r={r} fill="none" strokeWidth="2.5" className="stroke-blue-900/60" />
      <circle
        cx="12"
        cy="12"
        r={r}
        fill="none"
        strokeWidth="2.5"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - progress)}
        strokeLinecap="round"
        className="stroke-blue-400 transition-[stroke-dashoffset] duration-1000 ease-linear"
      />
    </svg>
  );
}

function TimeoutRow({ label, tooltip, remaining, isLow }: {
  label: string;
  tooltip: string;
  remaining: number;
  isLow: boolean;
}) {
  const mm = Math.floor(remaining / 60).toString();
  const ss = (remaining % 60).toString().padStart(2, '0');
  return (
    <div className="relative rounded-md border border-border/40 p-3 flex items-center justify-between gap-3 text-sm">
      <span className="text-muted flex items-center gap-1.5">
        {label}
        <span className="group inline-flex">
          <Info className="h-3.5 w-3.5 text-muted cursor-help" />
          <span className="pointer-events-none absolute left-0 bottom-full mb-1 z-10 whitespace-nowrap rounded-lg border border-border bg-surface-strong px-2 py-1 text-xs text-foreground shadow-md opacity-0 transition-opacity group-hover:opacity-100">
            {tooltip}
          </span>
        </span>
      </span>
      <div className="flex items-center gap-2">
        <CountdownCircle remaining={remaining} total={60} />
        <span className={`font-mono font-semibold tabular-nums ${isLow ? 'text-danger' : 'text-foreground'}`}>
          {mm}:{ss}
        </span>
      </div>
    </div>
  );
}

export function IdleCountdown() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const [now, setNow] = useState(() => Date.now() / 1000);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  const expiresAt = useMemo(() => {
    const stamps = heartbeats
      .filter((hb) => hb.heartbeat_id === 'arm_left' || hb.heartbeat_id === 'arm_right')
      .map((hb) => (hb.payload as { idle_expires_at?: number } | undefined)?.idle_expires_at)
      .filter((t): t is number => typeof t === 'number');
    return stamps.length > 0 ? Math.min(...stamps) : null;
  }, [heartbeats]);

  if (expiresAt == null) return null;
  const remaining = Math.max(0, Math.round(expiresAt - now));
  if (remaining >= 60) return null;

  return (
    <TimeoutRow
      label="Idle timeout"
      tooltip="Arms will overheat if they are left in the same postion for too long."
      remaining={remaining}
      isLow={remaining <= 10}
    />
  );
}

export function NavTimeoutCountdown() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const [now, setNow] = useState(() => Date.now() / 1000);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  const expiresAt = useMemo(() => {
    const fb = heartbeats.find((hb) => hb.heartbeat_id === 'flow_base_service');
    const t = (fb?.payload as { command_timeout_expires_at?: number | null } | undefined)
      ?.command_timeout_expires_at;
    return typeof t === 'number' ? t : null;
  }, [heartbeats]);

  if (expiresAt == null) return null;
  const remaining = Math.max(0, Math.round(expiresAt - now));
  if (remaining >= 60) return null;

  return (
    <TimeoutRow
      label="Navigation timeout"
      tooltip="Lidar will overheat if it is left running"
      remaining={remaining}
      isLow={remaining <= 10}
    />
  );
}

function TaskControls() {
  return (
    <div className="h-full w-full flex items-center justify-center p-4">
      <div className="w-full max-w-md flex flex-col gap-3">
        <IdleCountdown />
        <CheckpointPanel />
      </div>
    </div>
  );
}

function HealingBlock() {
  return (
    <div className="h-full w-full flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-md border border-orange-500/40 bg-orange-500/10 p-4 flex flex-col gap-2">
        <span className="text-sm font-semibold text-orange-500">Issue Detected</span>
        <p className="text-sm text-foreground leading-relaxed">
          An issue with the system has been detected, attempting to fix it.
        </p>
      </div>
    </div>
  );
}

export function DashboardMiddle() {
  const state = useRobotStore((s) => s.stateMachineState);
  const healingInProgress = useRobotStore((s) => s.healingInProgress);
  const isError = state?.toLowerCase() === 'error';

  let content;
  if (state === 'navigation') content = <NavigationControls />;
  else if (state === 'task_execution') content = <TaskControls />;
  else if (isError && healingInProgress) content = <HealingBlock />;
  else content = <URDFViewer />;

  return (
    <div className="relative h-full w-full">
      {content}
      {/* z-[80] keeps the e-stop / battery stack above the estop blur overlay
          in app-shell (z-[70]) — the overlay deliberately doesn't cover this
          stack so the user can still press Release E-Stop while everything
          else is disabled. */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-[80] flex flex-col items-center gap-2">
        <BatteryIcon />
        <EStopButton />
      </div>
    </div>
  );
}
